import React, { useState, useEffect } from "react";
import { FiX, FiFileText, FiImage, FiSave, FiUpload } from "react-icons/fi";
import styles from "../css/shared.module.css";

const BrainEntryModal = ({ isOpen, onClose, section, entry, onSave }) => {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("text");
  const [content, setContent] = useState("");
  const [imageData, setImageData] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Función para determinar si es la entrada de captura de pantalla
  const isScreenshotEntry = () => {
    const titleLower = title.toLowerCase();
    return titleLower.includes("captura") && titleLower.includes("rendimiento");
  };

  // Función para obtener el placeholder basado en el título
  const getContentPlaceholder = () => {
    if (!title.trim()) return "Escribe el contenido de tu entrada...";
    
    const titleLower = title.toLowerCase();
    if (titleLower.includes("demográfica") || titleLower.includes("avatar")) {
      return "Agrega aquí la información demográfica de tu avatar objetivo (edad, género, ubicación, etc.)";
    } else if (titleLower.includes("problemas") || titleLower.includes("audiencia")) {
      return "Lista los 3 principales problemas que enfrenta tu audiencia objetivo";
    } else if (titleLower.includes("foco")) {
      return "Define cuál es tu foco principal o tema central de contenido";
    } else if (titleLower.includes("captura") || titleLower.includes("rendimiento")) {
      return "Sube una captura de pantalla de tus videos con mejor rendimiento (opcional)";
    } else if (titleLower.includes("credibilidad")) {
      return "Describe tu credibilidad y experiencia en el tema";
    } else if (titleLower.includes("historia") || titleLower.includes("personal")) {
      return "Cuenta tu historia personal y cómo llegaste a donde estás";
    } else if (titleLower.includes("conocimiento") || titleLower.includes("espectador")) {
      return "Especifica el nivel de conocimiento que tiene tu audiencia sobre el tema";
    }
    
    return "Escribe el contenido de tu entrada...";
  };

  // Función para verificar si el contenido es un placeholder
  const isPlaceholderContent = (content, title) => {
    if (!content || !title) return false;
    
    const placeholders = [
      "Agrega aquí la información demográfica",
      "Lista los 3 principales problemas",
      "Define cuál es tu foco principal",
      "Sube una captura de pantalla",
      "Describe tu credibilidad",
      "Cuenta tu historia personal",
      "Especifica el nivel de conocimiento",
    ];
    
    return placeholders.some(placeholder => content.startsWith(placeholder));
  };

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (isOpen) {
      if (entry) {
        // Modo edición
        setTitle(entry.title || "");
        // Determinar el tipo basado en el título si es la entrada de captura de pantalla
        const entryTitle = entry.title || "";
        const isScreenshot = entryTitle.toLowerCase().includes("captura") && entryTitle.toLowerCase().includes("rendimiento");
        setType(isScreenshot ? "image" : (entry.type || "text"));
        // Limpiar contenido si es un placeholder
        const content = entry.content || "";
        const isPlaceholder = isPlaceholderContent(content, entry.title || "");
        setContent(isPlaceholder ? "" : content);
        setImageData(entry.imageData || "");
      } else {
        // Modo creación
        setTitle("");
        setType("text");
        setContent("");
        setImageData("");
        setImageFile(null);
      }
      setError("");
    }
  }, [isOpen, entry]);

  // Función para comprimir imagen
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calcular nuevas dimensiones manteniendo la proporción
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir a base64 con compresión
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Función para manejar la carga de imágenes
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        setError('Por favor selecciona un archivo de imagen válido');
        return;
      }
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen debe ser menor a 5MB');
        return;
      }

      setImageFile(file);
      setError("Comprimiendo imagen...");
      
      try {
        // Comprimir imagen antes de convertir a base64
        const compressedDataUrl = await compressImage(file);
        setImageData(compressedDataUrl);
        setError("");
      } catch (err) {
        setError('Error al procesar la imagen');
        console.error('Error compressing image:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError("El título de la entrada es requerido");
      return;
    }
    
    if (!content.trim()) {
      setError("El contenido de la entrada es requerido");
      return;
    }

    if (type === "image" && !imageData.trim()) {
      setError("La imagen es requerida para entradas de tipo imagen");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const entryData = {
        title: title.trim(),
        type,
        content: content.trim(),
        imageData: type === "image" ? imageData : null,
      };

      await onSave(entryData);
    } catch (err) {
      setError("Error al guardar la entrada");
      console.error("Error saving entry:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    if (newType === "text") {
      setImageData("");
      setImageFile(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            {type === "text" ? <FiFileText size={20} /> : <FiImage size={20} />}
            <span>{entry ? "Editar Entrada" : "Nueva Entrada"}</span>
          </div>
          <button
            onClick={handleClose}
            className={styles.modalCloseButton}
            disabled={isSubmitting}
          >
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.modalBody}>
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            {/* Información de la sección */}
            {section && (
              <div className={styles.entrySectionInfo}>
                <p><strong>Sección:</strong> {section.title}</p>
              </div>
            )}

            {/* Campo de título */}
            <div className={styles.formGroup}>
              <label htmlFor="entryTitle" className={styles.formLabel}>
                Título de la entrada *
              </label>
              <input
                id="entryTitle"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Conceptos de Programación"
                className={`${styles.input} ${styles.readOnlyInput}`}
                disabled={true}
                readOnly
                required
              />
            </div>

            {/* Selector de tipo */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Tipo de entrada</label>
              <div className={styles.typeSelector}>
                {/* Mostrar opción de texto solo si NO es la entrada de captura de pantalla */}
                {!isScreenshotEntry() && (
                  <button
                    type="button"
                    className={`${styles.typeButton} ${type === "text" ? styles.active : ""}`}
                    onClick={() => handleTypeChange("text")}
                    disabled={isSubmitting}
                  >
                    <FiFileText size={16} />
                    <span>Texto</span>
                  </button>
                )}
                
                {/* Mostrar opción de imagen solo si ES la entrada de captura de pantalla */}
                {isScreenshotEntry() && (
                  <button
                    type="button"
                    className={`${styles.typeButton} ${type === "image" ? styles.active : ""}`}
                    onClick={() => handleTypeChange("image")}
                    disabled={isSubmitting}
                  >
                    <FiImage size={16} />
                    <span>Imagen</span>
                  </button>
                )}
              </div>
            </div>

            {/* Contenido */}
            <div className={styles.formGroup}>
              {/* Solo mostrar label si es texto o si es imagen pero NO es la entrada de captura de pantalla */}
              {(type === "text" || (type === "image" && !isScreenshotEntry())) && (
                <label htmlFor="entryContent" className={styles.formLabel}>
                  {type === "text" ? "Contenido *" : "Descripción de la imagen *"}
                </label>
              )}
              {type === "text" ? (
                <textarea
                  id="entryContent"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className={`${styles.input} ${styles.textarea}`}
                  placeholder={getContentPlaceholder()}
                  rows={6}
                  disabled={isSubmitting}
                  required
                />
              ) : (
                // Solo mostrar campo de descripción si NO es la entrada de captura de pantalla
                !isScreenshotEntry() && (
                  <input
                    id="entryContent"
                    type="text"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className={styles.input}
                    placeholder={getContentPlaceholder()}
                    disabled={isSubmitting}
                    required
                  />
                )
              )}
            </div>

            {/* URL de imagen (solo para tipo imagen) */}
            {type === "image" && (
              <div className={styles.formGroup}>
                <label htmlFor="imageFile" className={styles.formLabel}>
                  Imagen *
                </label>
                <div className={styles.imageUploadInput}>
                  <input
                    id="imageFile"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className={styles.fileInput}
                    disabled={isSubmitting}
                    required
                  />
                  <label htmlFor="imageFile" className={styles.fileInputLabel}>
                    <FiUpload size={16} />
                    <span>{imageFile ? imageFile.name : "Seleccionar imagen"}</span>
                  </label>
                </div>
                <p className={styles.helpText}>
                  Selecciona una imagen desde tu dispositivo (máximo 5MB)
                </p>
              </div>
            )}

            {/* Vista previa de imagen */}
            {type === "image" && imageData && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Vista previa</label>
                <div className={styles.imagePreview}>
                  <img
                    src={imageData}
                    alt="Vista previa"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                  <div className={styles.imageError} style={{ display: "none" }}>
                    <FiImage size={32} />
                    <p>No se pudo cargar la imagen</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              onClick={handleClose}
              className={`${styles.button} ${styles.secondary}`}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`${styles.button} ${styles.primary}`}
              disabled={isSubmitting || !content.trim() || (type === "image" && !imageData.trim())}
            >
              <FiSave size={16} />
              <span>{isSubmitting ? "Guardando..." : "Guardar"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrainEntryModal;
